'use client';

import * as Dialog from '@radix-ui/react-dialog';
import { CaretLeft, CaretRight, X } from 'phosphor-icons';
import { useRef } from 'react';
import { c } from '../../../utils/tailwind-merge';
import { SheetsProps } from './Sheets.types';

export const Sheets = ({
  label,
  parentSheetsTitle,
  nestedModal,
  isFirstItem,
  children,
  open,
  onOpenChange,
  microcopy,
  className = '',
  contentClassName = '',
}: SheetsProps) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const cleanHash = () => {
    window.history.replaceState(
      null,
      '',
      window.location.pathname + window.location.search
    );
  };

  const handleCloseAll = async (e: React.MouseEvent<SVGSVGElement>) => {
    e.preventDefault();
    cleanHash();

    if (contentRef.current) {
      contentRef.current.setAttribute('data-state', 'closed');
    }

    await new Promise((resolve) => setTimeout(resolve, 300));
    onOpenChange?.(false);
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          cleanHash();
        }
        onOpenChange?.(isOpen);
      }}
    >
      <Dialog.Trigger asChild className={className}>
        <button className="w-full">
          <div
            className={c(
              'flex items-center justify-between border-b border-line-medium p-3',
              isFirstItem ? 'border-t' : ''
            )}
          >
            <span className="text-content-muted">{label}</span>
            <CaretRight className="h-6 w-6 text-content-subtle" />
          </div>
        </button>
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/30 transition-opacity duration-300 dark:bg-overlay" />
        <Dialog.Content
          className={c(
            'fixed right-0 top-0 z-50 h-full w-full max-w-md overflow-auto bg-surface-raised shadow-lg',
            'transition-transform duration-300 ease-in-out',
            'data-[state=closed]:animate-slideOut data-[state=open]:animate-slideIn'
          )}
          ref={contentRef}
        >
          <div className="flex items-center justify-between border-b border-line-medium px-2 py-3">
            <div className="flex flex-row items-center">
              <Dialog.Close asChild>
                <CaretLeft
                  className="h-6 w-6 cursor-pointer"
                  aria-label="Close"
                />
              </Dialog.Close>
              <Dialog.Title className="ml-2 text-base text-content-muted">
                {parentSheetsTitle ?? label}
              </Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <div className="flex flex-row items-center" id="close-all">
                <X
                  className="h-6 w-6 cursor-pointer text-content"
                  aria-label={microcopy?.close}
                  onClick={handleCloseAll}
                />
              </div>
            </Dialog.Close>
          </div>

          <div className={c('', children ? 'px-2.5' : '', contentClassName)}>
            {children ?? nestedModal}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
